import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlertTriangle, ArrowRight, Users, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const sessionToken =
    localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
};

const formatINR = (n) => {
  const v = Number(n) || 0;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export default function AdminDuplicates() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeDialog, setMergeDialog] = useState(null); // { primary, secondary }
  const [merging, setMerging] = useState(false);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/partners/duplicates`, { headers: getAuthHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to load');
      setGroups(d.groups || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const handleMerge = async () => {
    if (!mergeDialog) return;
    setMerging(true);
    try {
      const r = await fetch(`${API}/admin/partners/merge`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_user_id: mergeDialog.primary.user_id,
          secondary_user_id: mergeDialog.secondary.user_id,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Merge failed');
      toast.success(
        `Merged: ${d.moved_referrals} referrals, ${d.moved_transactions} txns, ` +
          `${d.moved_payouts} payouts moved. Commission +${formatINR(d.added_total_commission)}.`
      );
      setMergeDialog(null);
      fetchDuplicates();
    } catch (e) {
      toast.error(e.message || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-duplicates-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Possible Duplicate Partners</h1>
          <p className="text-muted-foreground mt-1">
            Partners whose name or phone number matches another active partner. Same name with
            different emails usually means the same person signed up twice — review carefully and
            merge if appropriate.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchDuplicates}
          disabled={loading}
          data-testid="duplicates-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
            <p className="mt-2">Scanning partners...</p>
          </CardContent>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
            <h3 className="text-lg font-semibold">No duplicates found 🎉</h3>
            <p className="text-muted-foreground">
              No partners share a normalized name or phone number.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <Card key={gi} data-testid={`dup-group-${gi}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Match by {g.match_type}: <span className="font-mono">{g.match_value}</span>
                  <Badge variant="secondary">{g.partners.length} accounts</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2">
                  {g.partners.map((p) => (
                    <div
                      key={p.user_id}
                      className="border rounded-lg p-3 bg-card"
                      data-testid={`dup-partner-${p.user_id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-semibold">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{p.email}</div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs">
                          {p.referral_code}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <div>Phone: {p.phone || '—'}</div>
                        <div>Tier: {p.tier || 'silver'}</div>
                        <div>Refs: {p.referral_count}</div>
                        <div>Txns: {p.transaction_count}</div>
                        <div>Total ₹: {formatINR(p.total_commission)}</div>
                        <div>Pending ₹: {formatINR(p.pending_commission)}</div>
                        <div>Created: {fmtDate(p.created_at)}</div>
                        <div>Last login: {fmtDate(p.last_login)}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Merge controls — only enable for pairs */}
                {g.partners.length >= 2 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {g.partners.map((primary) =>
                      g.partners
                        .filter((s) => s.user_id !== primary.user_id)
                        .map((secondary) => (
                          <Button
                            key={`${primary.user_id}-${secondary.user_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setMergeDialog({ primary, secondary })}
                            data-testid={`merge-btn-${primary.user_id}-${secondary.user_id}`}
                          >
                            Merge <span className="font-semibold mx-1">{secondary.name}</span> (
                            {secondary.referral_code}) <ArrowRight className="h-3 w-3 mx-1" />
                            <span className="font-semibold">{primary.name}</span> (
                            {primary.referral_code})
                          </Button>
                        ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Merge confirmation dialog */}
      <Dialog open={!!mergeDialog} onOpenChange={(open) => !open && setMergeDialog(null)}>
        <DialogContent data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Merge</DialogTitle>
            <DialogDescription>
              This action moves all data from the secondary into the primary. The secondary
              account will be marked as <strong>merged</strong> and any future logins via that
              email will transparently resolve to the primary account.{' '}
              <span className="text-red-600">This cannot be easily undone.</span>
            </DialogDescription>
          </DialogHeader>

          {mergeDialog && (
            <div className="space-y-3 my-2">
              <div className="rounded border bg-green-50 border-green-200 p-3">
                <div className="text-xs text-green-700 font-semibold uppercase tracking-wide">
                  Primary (kept)
                </div>
                <div className="font-semibold">{mergeDialog.primary.name}</div>
                <div className="text-sm text-muted-foreground">
                  {mergeDialog.primary.email} · code <strong>{mergeDialog.primary.referral_code}</strong>
                </div>
                <div className="text-xs mt-1">
                  Refs: {mergeDialog.primary.referral_count} · Txns: {mergeDialog.primary.transaction_count} ·
                  Pending ₹{formatINR(mergeDialog.primary.pending_commission)}
                </div>
              </div>
              <div className="rounded border bg-red-50 border-red-200 p-3">
                <div className="text-xs text-red-700 font-semibold uppercase tracking-wide">
                  Secondary (merged & disabled)
                </div>
                <div className="font-semibold">{mergeDialog.secondary.name}</div>
                <div className="text-sm text-muted-foreground">
                  {mergeDialog.secondary.email} · code{' '}
                  <strong>{mergeDialog.secondary.referral_code}</strong>
                </div>
                <div className="text-xs mt-1">
                  Refs: {mergeDialog.secondary.referral_count} · Txns:{' '}
                  {mergeDialog.secondary.transaction_count} · Pending ₹
                  {formatINR(mergeDialog.secondary.pending_commission)}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                After merge, {mergeDialog.secondary.referral_count} referrals,{' '}
                {mergeDialog.secondary.transaction_count} transactions, and{' '}
                {formatINR(mergeDialog.secondary.total_commission)} commission will be moved to{' '}
                {mergeDialog.primary.name}.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeDialog(null)} disabled={merging}>
              Cancel
            </Button>
            <Button onClick={handleMerge} disabled={merging} data-testid="merge-confirm-button">
              {merging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                'Confirm Merge'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
